import React, { useCallback, useMemo } from 'react';
import { Wallet, BadgeCheck, TrendingUp, ChevronRight } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { usePmUnits, nextPayout, leaseBook, rentAccrual } from './portfolioMetrics';
import LiveTicker from './LiveTicker';

/**
 * NEXT PAYOUT and PROFIT THIS YEAR, on the owner's Home screen.
 *
 * The two questions an owner with a let property actually opens the app with:
 * when does the next rent reach me, and what has the year made. Both were
 * answerable only by opening Property Management and reading a cheque table.
 *
 * Every figure comes from signed tenancy contracts and costs booked against
 * the unit — see nextPayout / leaseBook. Two things in the reference design
 * are deliberately absent because the product does not hold them:
 *
 *   • the masked bank account ("AE123A***56") — no payout account is stored
 *     anywhere, so there is nothing to mask;
 *   • a full-green card — the corporate palette is gold, white and black.
 *     Green survives only where the module already uses it, as the functional
 *     mark for money received.
 */
// Functional green, the same pair the owner module already uses for
// rented / paid. It appears ONLY on money received — never as a surface.
const GREEN_INK = '#0b7a53';

interface Props {
  investorId?: string;
  /** Showcase / film: PM traffic is blocked, so fixtures come in directly. */
  unitsOverride?: any[];
  onOpen?: () => void;
}

const PayoutSummary: React.FC<Props> = ({ investorId, unitsOverride, onOpen }) => {
  const { t, language } = useLanguage();
  const units = usePmUnits(investorId, unitsOverride);

  const payout = useMemo(() => nextPayout(units), [units]);
  const book = useMemo(() => leaseBook(units), [units]);
  // Rent accruing from the signed leases, counted from the clock — see
  // rentAccrual. Recomputed per render of `units`, not per tick.
  const accrual = useMemo(() => rentAccrual(units), [units]);
  const rentAt = useCallback(
    (ms: number) => accrual.earnedThisYear + accrual.perSecond * Math.max(0, (ms - accrual.asOf) / 1000),
    [accrual],
  );

  // Nothing let, nothing banked: the cards would be a row of zeroes.
  if (!payout && book.rows.length === 0) return null;

  const locale = language === 'de' ? 'de-DE' : 'en-AE';
  const aed = (n: number) => n.toLocaleString(locale, { maximumFractionDigits: 0 });
  const date = (raw: string) => {
    const d = raw ? new Date(raw) : null;
    if (!d || isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-GB',
      { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <>
      {payout && (
        <div className="ps-card">
          <div className="ps-head">
            <button className="ps-eyebrow ps-eyebrow--btn" onClick={onOpen}>
              <Wallet size={13} strokeWidth={2.2} /> {t('ipNextPayout')}
              <ChevronRight size={13} strokeWidth={2.6} />
            </button>
            <span className="ps-chip">
              <BadgeCheck size={12} strokeWidth={2.4} /> {t('ipChequeHeld')}
            </span>
          </div>

          <h2 className="ps-value"><span className="cur">AED</span>{aed(payout.amount)}</h2>

          {/* One segment per cheque in the tenancy year, filled as each is
              banked — the cheque run at a glance, which is how rent in Dubai
              actually arrives. */}
          <div className="ps-bar" role="img"
            aria-label={`${payout.collected} / ${payout.total}`}>
            {Array.from({ length: payout.total }).map((_, i) => (
              <span key={i} className={i < payout.collected ? 'on' : ''} />
            ))}
          </div>
          <p className="ps-note">
            {t('ipPayoutProgress')
              .replace('{n}', String(payout.collected))
              .replace('{total}', String(payout.total))}
          </p>

          <div className="ps-meta">
            <div>
              <span className="ps-label">{t('ipPayoutDate')}</span>
              <span className="ps-fact">{date(payout.dueDate)}</span>
            </div>
            <div>
              <span className="ps-label">{t('ipPayoutProperty')}</span>
              <span className="ps-fact">{payout.unitName || '—'}</span>
            </div>
          </div>

          {/* The payout after it, so an owner with several leases sees the
              next two cheques at once (owner request 2026-11-05). */}
          {payout.following && (
            <div className="ps-meta">
              <div>
                <span className="ps-label">{language === 'de' ? 'Danach' : 'Then'}</span>
                <span className="ps-fact">{date(payout.following.dueDate)} · AED {aed(payout.following.amount)}</span>
              </div>
              <div>
                <span className="ps-label">{t('ipPayoutProperty')}</span>
                <span className="ps-fact">{payout.following.unitName || '—'}</span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="ps-card ps-card--paper">
        <div className="ps-head">
          <button className="ps-eyebrow ps-eyebrow--ink ps-eyebrow--btn" onClick={onOpen}>
            <TrendingUp size={13} strokeWidth={2.2} /> {t('ipProfitAllLeases')}
            <ChevronRight size={13} strokeWidth={2.6} />
          </button>
          {book.netYield !== null && (
            <span className="ps-roi">
              {book.netYield.toFixed(1)}%
              <em>{t('ipNetYieldPa')}</em>
            </span>
          )}
        </div>

        <h2 className="ps-value ps-value--ink"><span className="cur">AED</span>{aed(book.net)}</h2>
        {/* Says outright that this is the RENTAL result — not a capital gain,
            not the portfolio's value change, which sit on the card above. */}
        <p className="ps-note ps-note--ink">
          {t('ipLeaseBasis').replace('{n}', String(book.letUnits))}
        </p>

        {accrual.leases > 0 && (
          <div className="ps-live">
            <span className="ps-live-label">
              <span className="ps-live-dot" aria-hidden="true" />
              {t('ipRentLive').replace('{year}', String(new Date().getFullYear()))}
            </span>
            <div className="ps-live-value">
              <span className="cur">AED</span>
              <LiveTicker at={rentAt} locale={locale} />
            </div>
            <p className="ps-live-sub">
              {t('ipRentPerDay')
                .replace('{day}', aed(accrual.perDay))
                .replace('{n}', String(accrual.leases))}
            </p>
          </div>
        )}

        <div className="ps-kpis">
          <div>
            <span className="ps-label">{t('ipIncome')}</span>
            <span className="ps-sum" style={{ color: GREEN_INK }}>AED {aed(book.rent)}</span>
          </div>
          <div>
            <span className="ps-label">{t('ipExpenses')}</span>
            <span className="ps-sum">AED {aed(book.costs)}</span>
          </div>
          <div>
            <span className="ps-label">{t('ipCollected')}</span>
            <span className="ps-sum">AED {aed(book.collected)}</span>
            {book.rent > 0 && (
              <span className="ps-sub">{Math.round((book.collected / book.rent) * 100)}% {t('ipOfLeaseRent')}</span>
            )}
          </div>
          <div>
            <span className="ps-label">{t('ipUnitsLet')}</span>
            <span className="ps-sum">{book.letUnits} / {book.totalUnits}</span>
          </div>
        </div>

        {/* One row per signed lease, soonest to end first — the renewal
            decision that is closest leads. */}
        <div className="ps-leases">
          {book.rows.map((r) => (
            <div key={r.unitId} className="ps-lease">
              <div className="ps-lease-top">
                <span className="ps-lease-name">{r.name}</span>
                <span className="ps-lease-net" style={{ color: r.net >= 0 ? GREEN_INK : '#000' }}>
                  AED {aed(r.net)}
                </span>
              </div>
              <div className="ps-lease-term">
                {date(r.start)} – {date(r.end)} · {t('ipMonthsN').replace('{n}', String(r.months))}
              </div>
              <div className="ps-lease-figs">
                <span>{t('ipRentShort')} <b>{aed(r.rent)}</b></span>
                <span>
                  {t('ipCostsShort')} <b>{r.serviceCharge === null && r.booked === 0 ? '—' : aed(r.booked + (r.serviceCharge || 0))}</b>
                </span>
                {r.netYield !== null && (
                  <span>{t('ipYieldShort')} <b>{r.netYield.toFixed(1)}%</b></span>
                )}
                <span>{t('ipChequesShort')} <b>{r.chequesBanked}/{r.chequesTotal}</b></span>
              </div>
              <div className="ps-lease-bar" role="img"
                aria-label={`${Math.round(r.elapsed * 100)}%`}>
                <span style={{ width: `${Math.round(r.elapsed * 100)}%` }} />
              </div>
              <div className="ps-lease-foot">
                <span>{t('ipLeasePassed').replace('{p}', String(Math.round(r.elapsed * 100)))}</span>
                <span className={r.daysLeft <= 90 ? 'ps-lease-soon' : ''}>
                  {r.daysLeft > 0
                    ? t('ipDaysLeft').replace('{n}', String(r.daysLeft))
                    : t('ipLeaseEnded')}
                </span>
              </div>
              {r.serviceCharge === null && (
                <p className="ps-lease-note">{t('ipScUnpublished')}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default PayoutSummary;
